// Build release… (spec/dashboard.md section 12.2, Q-C-5): the dry run posts itself when the dialog opens — a
// `release.build` job followed with JobProgress, its result rendered as the deploy diff (the field-level changes against
// the branch's previous release, the file paths kept under "Files") —, then Build: the real job, its progress, the toast
// (or the owner's `onBuilt`) and the re-read. Build stays gated on a successful dry run of the current form: an edit of the
// commit or the assets checkbox resets the preview and re-runs it (the commit 600 ms after its last keystroke); the notes
// do not change the diff, so a notes edit neither resets nor re-runs.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ops } from '../../api/operations';
import type { Job, Release } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ValidationReport } from '../../history/ValidationReport';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { Checkbox, TextArea, TextInput } from '../../ui/inputs';
import { JobProgress } from '../../ui/JobProgress';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError, builtRelease, countEntries, DONE, issuesOf, keys, type DryRunResult, type JobError } from './releases';
import { ReleaseDiff } from './ReleaseDiff';
import '../org/org.css';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { ChevronRight } from '../../ui/icons';

/** The pause after the last keystroke in the Commit field before the dry run re-runs. */
export const DRY_RUN_DEBOUNCE_MS = 600;

export interface BuildDialogProps {
  branch: string;
  open: boolean;
  onClose: () => void;
  missingPermission: string | null;
  /** called with the built release after the caches were invalidated; when given, the dialog's own toast is skipped */
  onBuilt?: (release: Partial<Release>) => void;
}

export function BuildDialog({ branch, open, onClose, missingPermission, onBuilt }: BuildDialogProps) {
  const { org, game, missing } = useGame();
  const { client } = useAuth();
  const jobApi = { client, org, game: game.slug, missingPermission: missing('content_write') };
  const toasts = useToasts();
  const [commit, setCommit] = useState('');
  const [notes, setNotes] = useState('');
  const [includeAssets, setIncludeAssets] = useState(false);
  const [posting, setPosting] = useState<'dry' | 'build' | null>(null);
  const [dryJob, setDryJob] = useState<Job | null>(null);
  const [dryDone, setDryDone] = useState<Job | null>(null);
  /** the preview no longer describes the form (an edit reset it) or its post was refused: Run again shows */
  const [stale, setStale] = useState(false);
  const [buildJob, setBuildJob] = useState<Job | null>(null);
  const [buildDone, setBuildDone] = useState<Job | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose; // the owner passes an inline closure: the poll must not restart on its renders
  const onBuiltRef = useRef(onBuilt);
  onBuiltRef.current = onBuilt;
  /** bumped by every reset: a dry-run post answering after an edit belongs to a form that no longer exists */
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRan = useRef(false);

  const cancelTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => cancelTimer, []);

  const reset = () => {
    generation.current++;
    setDryJob(null);
    setDryDone(null);
    setBuildJob(null);
    setBuildDone(null);
  };
  const close = () => {
    cancelTimer();
    reset();
    setStale(false);
    setPosting(null);
    setCommit('');
    setNotes('');
    setIncludeAssets(false);
    onClose();
  };

  const onBuildDone = useCallback(
    (job: Job) => {
      setBuildDone(job);
      if (job.status !== 'succeeded') return;
      const release = builtRelease(job);
      queryCache.invalidate(keys.releasesPrefix(org, game.slug));
      queryCache.invalidate(keys.jobs(org, game.slug));
      const owner = onBuiltRef.current;
      if (owner) owner(release ?? {});
      else toasts.success(release?.number !== undefined && release?.number !== null ? `Release #${release.number} built` : 'Release built');
      generation.current++;
      setDryJob(null);
      setDryDone(null);
      setBuildJob(null);
      setBuildDone(null);
      setStale(false);
      setCommit('');
      setNotes('');
      setIncludeAssets(false);
      onCloseRef.current();
    },
    [toasts, org, game.slug],
  );
  const onDryDone = useCallback((job: Job) => setDryDone(job), []);

  const post = async (dryRun: boolean) => {
    if (dryRun) cancelTimer();
    const gen = generation.current;
    // the real build ships the commit the dry run previewed (its resolved sha), so a head that moved meanwhile cannot change what the diff showed
    const previewed = !dryRun && dryDone?.status === 'succeeded' ? (dryDone.result as { commit?: string } | null | undefined)?.commit : undefined;
    const sha = previewed ?? commit.trim();
    const body = { ...(dryRun ? { dryRun: true } : {}), ...(sha ? { commit: sha } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}), includeAssetsWithoutContent: includeAssets };
    setPosting(dryRun ? 'dry' : 'build');
    if (dryRun) setStale(false);
    try {
      const job = (await client.data(ops.buildRelease, { path: { org, game: game.slug, branch }, body })) as Job;
      queryCache.invalidate(keys.jobs(org, game.slug));
      if (dryRun) {
        if (gen !== generation.current) return; // an edit came in meanwhile: its own dry run follows
        setDryJob(job);
        setDryDone(DONE.has(job.status) ? job : null);
      } else {
        setBuildJob(job);
        if (DONE.has(job.status)) onBuildDone(job);
      }
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
      if (dryRun && gen === generation.current) setStale(true);
    } finally {
      // a superseded dry run leaves the busy state to the one that replaced it (the edit always schedules one)
      if (!dryRun || gen === generation.current) setPosting(null);
    }
  };
  const postRef = useRef(post);
  postRef.current = post; // the debounce fires after the edit rendered: it posts the form as it is then

  // the automatic dry run: posted as the dialog opens, so the diff is already loading when it appears
  useEffect(() => {
    if (!open) {
      autoRan.current = false;
      return;
    }
    if (autoRan.current || missingPermission) return;
    autoRan.current = true;
    void postRef.current(true);
  }, [open, missingPermission]);

  /** An edit that changes what the build would ship: the preview resets and the dry run re-runs after `delay`. */
  const editDiff = <T,>(set: (v: T) => void, delay: number) => (v: T) => {
    set(v);
    if (buildJob) return;
    reset(); // the preview describes the form it ran with
    setStale(true);
    cancelTimer();
    if (missingPermission) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      void postRef.current(true);
    }, delay);
  };

  const dryOk = dryDone?.status === 'succeeded';
  const building = !!buildJob && !buildDone;
  const dryRunning = posting === 'dry' || (!!dryJob && !dryDone);
  const dryFailed = !!dryDone && dryDone.status !== 'succeeded';
  const showRunAgain = !buildJob && !dryRunning && (stale || dryFailed);
  const buildTitle = dryOk ? undefined : dryRunning ? 'The dry run is still running' : 'The build waits for a successful dry run';
  return (
    <Modal title="Build release" open={open} onClose={close} size="l">
      <div data-testid="build-dialog" className="build-dialog">
        <p className="muted small">
          Branch <code>{branch}</code>. The build validates the commit, publishes every file by content hash and the manifest last; the dry run runs by itself and shows what changes against the branch's previous release.
        </p>
        <div className="form-grid">
          <Field label="Commit" help="Leave empty for the branch head.">
            <TextInput className="mono" aria-label="Commit" placeholder="the branch head" value={commit} onChange={(e) => editDiff(setCommit, DRY_RUN_DEBOUNCE_MS)(e.target.value)} disabled={!!buildJob} autoComplete="off" />
          </Field>
          <div className="field build-check">
            <Checkbox label="Include assets without content" checked={includeAssets} onChange={(e) => editDiff(setIncludeAssets, 0)(e.target.checked)} disabled={!!buildJob} />
          </div>
          <Field className="span-2" label="Notes">
            <TextArea aria-label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!!buildJob} />
          </Field>
        </div>

        {dryJob ? (
          <section className="build-step" aria-label="Dry run">
            <h3>Dry run</h3>
            <JobProgress job={dryJob} api={jobApi} onDone={onDryDone} onRetry={(j) => { setDryJob(j); setDryDone(null); }} />
            {dryDone && <JobOutcome job={dryDone} branch={branch} />}
          </section>
        ) : (
          dryRunning && (
            <section className="build-step" aria-label="Dry run">
              <h3>Dry run</h3>
              <LoadingPane rows={2} label="Starting the dry run" />
            </section>
          )
        )}
        {buildJob && (
          <section className="build-step" aria-label="Build">
            <h3>Build</h3>
            <JobProgress job={buildJob} api={jobApi} onDone={onBuildDone} onRetry={(j) => { setBuildJob(j); setBuildDone(null); }} />
            {buildDone && buildDone.status !== 'succeeded' && <JobOutcome job={buildDone} branch={branch} />}
          </section>
        )}
      </div>
      <div className="modal-footer form-footer">
        <Button onClick={close}>{buildDone ? 'Close' : 'Cancel'}</Button>
        {showRunAgain && (
          <Button variant="ghost" onClick={() => void post(true)} missingPermission={missingPermission}>
            Run again
          </Button>
        )}
        <Button variant="primary" onClick={() => void post(false)} busy={posting === 'build' || building} disabled={!dryOk || (!!buildJob && !buildDone) || buildDone?.status === 'succeeded'} missingPermission={missingPermission} title={buildTitle}>
          Build
        </Button>
      </div>
    </Modal>
  );
}

/** A finished job: the deploy diff of a dry run, or the error with the validation issues of a failed build. */
function JobOutcome({ job, branch }: { job: Job; branch: string }) {
  if (job.status === 'succeeded') {
    const r = job.result as DryRunResult | null;
    return r ? <DeployDiff result={r} branch={branch} /> : null;
  }
  const error = job.error as JobError | null;
  const issues = issuesOf(error);
  return (
    <div className="build-failure">
      <LoadError>{error?.message ?? `The job ${job.status}.`}</LoadError>
      {issues && <ValidationReport issues={issues} branch={branch} errors={issues.filter((i) => i.severity === 'error').length} />}
    </div>
  );
}

/** One release by its id (immutable, so cached under the key the players' release catalog shares). */
function usePreviousRelease(releaseId: string | null) {
  const { org, game } = useGame();
  const { client } = useAuth();
  return useQuery(releaseId ? `game:${org}/${game.slug}:release:${releaseId}` : null, () => client.data(ops.getRelease, { path: { org, game: game.slug, release: releaseId! } }) as unknown as Promise<Release>);
}

/** The dry run's deploy diff: the stat strip, the facts, the field-level changes against the previous release, the files. */
export function DeployDiff({ result, branch }: { result: DryRunResult; branch: string }) {
  const changes = result.changes ?? {};
  const counts = countEntries(result.counts);
  const previousId = result.previousReleaseId ?? null;
  const previous = usePreviousRelease(previousId);
  const list = (label: string, paths: string[] | undefined, testid: string) => (
    <details className="diff-list" data-testid={testid}>
      <summary>
        <ChevronRight className="icon-s diff-list-chevron" aria-hidden="true" />
        {label} ({paths?.length ?? 0})
      </summary>
      {paths && paths.length > 0 ? (
        <ul>
          {paths.map((p) => (
            <li key={p}>
              <code>{p}</code>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">None.</p>
      )}
    </details>
  );
  let fields: ReactNode;
  if (!previousId) fields = <p className="muted small" data-testid="deploy-first">First release of this branch: every document ships.</p>;
  else if (!result.commit) fields = <p className="muted small">The dry run named no commit: the field changes cannot be shown.</p>;
  else if (previous.error) fields = <LoadError>{asApiError(previous.error).message}</LoadError>;
  else if (!previous.data) fields = <LoadingPane rows={3} label="Reading the previous release" />;
  else fields = <ReleaseDiff branch={branch} fromCommit={previous.data.commit ?? null} toCommit={result.commit} emptyMessage="No document changes against the previous release." />;
  return (
    <div className="deploy-diff pane" data-testid="deploy-diff">
      <div className="pane-head">
        <h3>Deploy diff</h3>
      </div>
      <div className="pane-body">
        <div className="stat-strip deploy-stats">
          <div className="stat">
            <span className="stat-label">Files to upload</span>
            <span className="stat-value" data-testid="deploy-upload">{String(result.wouldUpload ?? 0)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Reused</span>
            <span className="stat-value" data-testid="deploy-reuse">{String(result.wouldReuse ?? 0)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Files</span>
            <span className="stat-value">{String(result.files ?? '—')}</span>
          </div>
        </div>
        <dl className="kv deploy-facts">
          <dt>Compared with</dt>
          <dd data-testid="deploy-previous">
            {previousId ? (
              <>
                {previous.data?.number !== undefined && previous.data?.number !== null ? `#${previous.data.number} ` : ''}
                <code>{previousId}</code>
              </>
            ) : (
              'first release of this branch'
            )}
          </dd>
          <dt>Release id</dt>
          <dd>{result.releaseId ? <code>{result.releaseId}</code> : '—'}</dd>
          <dt>Commit</dt>
          <dd>{result.commit ? <code title={result.commit}>{result.commit.slice(0, 10)}</code> : '—'}</dd>
          <dt>Content hash</dt>
          <dd>{result.contentHash ? <code className="small">{result.contentHash}</code> : '—'}</dd>
        </dl>
        {counts.length > 0 && (
          <div className="row counts" aria-label="Counts">
            {counts.map(([k, v]) => (
              <Chip key={k}>
                {k}: {v}
              </Chip>
            ))}
          </div>
        )}
        <section className="deploy-fields" data-testid="deploy-fields" aria-label="Changes">
          {fields}
        </section>
        <details className="deploy-files" data-testid="deploy-files">
          <summary>
            <ChevronRight className="icon-s diff-list-chevron" aria-hidden="true" />
            Files
          </summary>
          <div className="deploy-files-body">
            {list('Added', changes.added, 'deploy-added')}
            {list('Changed', changes.changed, 'deploy-changed')}
            {list('Removed', changes.removed, 'deploy-removed')}
            <p className="small" data-testid="deploy-unchanged">
              {String(changes.unchanged ?? 0)} unchanged
            </p>
          </div>
        </details>
      </div>
    </div>
  );
}
